<template>
  <div class="MyMap">
    <div
      class="MyMap__mapWrapper"
      :class="{ 'MyMap__mapWrapper--drawMode': myMapStore.mapDrawApi?.isDrawActive?.value }"
      @dragover.prevent="onMapDragover"
      @dragleave.prevent="onMapDragleave"
      @drop.prevent="onMapDrop"
    >
      <Map
        class="MyMap__map"
        ref="my-map"
        :basemap="basemap"
        @map-ready="onMapReady"
        @basemap-changed="onBasemapChanged"
      />
      <Transition name="drop-overlay">
        <div v-show="isDraggingOverMap" class="MyMap__dropOverlay" aria-hidden="true">
          <div class="MyMap__dropOverlayContent">
            <span class="MyMap__dropOverlayText">{{ $t('geodata.dragDrop.dropOnMap') }}</span>
            <span class="MyMap__dropOverlayHint">{{ $t('geodata.dragDrop.dropOnMapHint') }}</span>
          </div>
        </div>
      </Transition>
    </div>
    <ScaleControl ref="scale-control" />
    <BottomRightMapControls ref="bottom-right-map-controls" />
    <BasemapPicker ref="basemap-picker" v-model="basemap" />
    <ToggleSidebarControl
      v-model="myMapStore.isLeftSidebarShown"
      :inversed-direction="true"
      :is-higlighted-when-off="true"
      ref="toggle-left-sidebar-control"
    />
    <MyMapItemPopup />
    <CommuneLayerHandler v-if="myMap" :my-map="myMap" />
    <FondLayerHandler
      v-if="myMap && myMapStore.fondLayerStyles.length"
      :key="mapInstance ? 'map-ready' : 'map-pending'"
      :my-map="myMap"
      :map-instance="mapInstance"
    />
    <OsmLayerHandler v-if="myMap" :my-map="myMap" :map-instance="mapInstance" />
  </div>
</template>

<script setup lang="ts">
import BasemapPicker from '@/components/map/controls/BasemapPicker.vue'
import BottomRightMapControls from '@/components/map/controls/BottomRightMapControls.vue'
import ToggleSidebarControl from '@/components/map/controls/ToggleSidebarControl.vue'
import Map from '@/components/map/Map.vue'
import type Basemap from '@/models/interfaces/map/Basemap'
import MapService, { IControl } from '@/services/map/MapService'
import { useMyMapStore } from '@/stores/myMapStore'
import { useMapDraw } from '@/composables/useMapDraw'
import {
  CAMEROON_MAP_VIEW_FIT_PADDING,
  createCameroonLngLatBounds,
} from '@/config/cameroonMapBounds'
import { nextTick, onBeforeUnmount, onMounted, ref, shallowRef, useTemplateRef, watch } from 'vue'
import { useRoute } from 'vue-router'
import MyMapItemPopup from '@/views/map/components/MyMapItemPopup.vue'
import ScaleControl from '@/components/map/controls/ScaleControl.vue'
import CommuneLayerHandler from '@/views/map/components/CommuneLayerHandler.vue'
import FondLayerHandler from '@/views/map/components/FondLayer/FondLayerHandler.vue'
import OsmLayerHandler from '@/views/map/components/OsmLayerHandler.vue'
import { NotificationType } from '@/models/enums/app/NotificationType'
import { addNotification } from '@/services/notifications/NotificationService'
import maplibregl from 'maplibre-gl'
import { useI18n } from 'vue-i18n'

type MapType = InstanceType<typeof Map>
const { t } = useI18n()
const basemap = ref<Basemap>()

const myMapStore = useMyMapStore()
const route = useRoute()
const isDraggingOverMap = ref(false)

function onMapDragover() {
  isDraggingOverMap.value = true
}

function onMapDragleave() {
  isDraggingOverMap.value = false
}

async function onMapDrop(event: DragEvent) {
  isDraggingOverMap.value = false
  const file = event.dataTransfer?.files?.[0]
  if (!file) return
  if (!myMapStore.myMap?.map) {
    addNotification(
      t('geodata.dragDrop.mapNotReady', "La carte n'est pas prête. Réessayez dans un instant."),
      NotificationType.INFO,
    )
    return
  }
  try {
    const { name, geojson } = await MapService.parseGeoJsonFromFile(file)
    const added = myMapStore.addUserDroppedLayer(name, geojson)
    if (added) {
      addNotification(
        t('geodata.dragDrop.success', 'Données géographiques ajoutées à la carte.'),
        NotificationType.SUCCESS,
      )
    } else {
      addNotification(
        t('geodata.dragDrop.mapNotReady', "La carte n'est pas prête. Réessayez dans un instant."),
        NotificationType.INFO,
      )
    }
  } catch (err) {
    const message = getGeodataErrorMessage(err)
    addNotification(message, NotificationType.ERROR)
  }
}

function getGeodataErrorMessage(err: unknown): string {
  const msg = err instanceof Error ? err.message : ''
  if (
    msg.includes('Only GeoJSON, KML, or KMZ') ||
    msg.includes('only .geojson') ||
    msg.includes('only .kml')
  ) {
    return t('geodata.dragDrop.badExtension')
  }
  if (msg.includes('Invalid GeoJSON') || msg.includes('unknown or missing type')) {
    return t('geodata.dragDrop.onlyGeoJsonFormat')
  }
  if (
    msg.includes('Invalid KML') ||
    msg.includes('KMZ archive contains no') ||
    msg.includes('conversion produced no')
  ) {
    return t('geodata.dragDrop.invalidKmlKmz')
  }
  return t('geodata.dragDrop.parseError')
}
const myMap = useTemplateRef<MapType>('my-map')
const toggleLeftSidebarControl = useTemplateRef('toggle-left-sidebar-control')
const scaleControl = useTemplateRef('scale-control')
const bottomRightMapControls = useTemplateRef('bottom-right-map-controls')
const basemapPicker = useTemplateRef('basemap-picker')

const mapInstance = shallowRef<maplibregl.Map | null>(null)

const drawApi = useMapDraw(mapInstance)
myMapStore.setMapDrawApi(drawApi)

// Apply pending draw restore whenever either the draw instance or the pending data becomes available.
function tryApplyPendingDrawRestore(): void {
  const pendingRestore = myMapStore.pendingDrawRestore
  if (pendingRestore && mapInstance.value && drawApi.draw.value) {
    drawApi.restoreFeatures(pendingRestore.features, pendingRestore.annotations)
    myMapStore.pendingDrawRestore = null
  }
}

watch(() => myMapStore.pendingDrawRestore, tryApplyPendingDrawRestore)
watch(drawApi.draw, tryApplyPendingDrawRestore)

function tryApplyPendingUserDroppedRestore(): void {
  if (myMapStore.pendingUserDroppedRestore?.length && mapInstance.value) {
    myMapStore.applyPendingUserDroppedRestore()
  }
}
watch(() => myMapStore.pendingUserDroppedRestore, tryApplyPendingUserDroppedRestore, { deep: true })

onBeforeUnmount(() => {
  myMapStore.setMapDrawApi(null)
})

function onMapReady(rawMap: maplibregl.Map) {
  try {
    mapInstance.value = rawMap

    drawApi.init(rawMap)

    // Apply any pending draw restore that was already set (fast path: initMapLayers completed first).
    if (myMapStore.pendingDrawRestore) {
      drawApi.restoreFeatures(
        myMapStore.pendingDrawRestore.features,
        myMapStore.pendingDrawRestore.annotations,
      )
      myMapStore.pendingDrawRestore = null
    }

    myMapStore.applyPendingUserDroppedRestore()

    rawMap.addControl(new IControl(toggleLeftSidebarControl), 'top-left')
    rawMap.addControl(new IControl(bottomRightMapControls), 'top-right')
    rawMap.addControl(new IControl(basemapPicker), 'bottom-right')
    rawMap.addControl(new IControl(scaleControl), 'bottom-left')

    if (route.query.mapState) {
      if (myMapStore.bbox) {
        const bboxRaw = myMapStore.bbox as {
          _sw?: { lng: number; lat: number }
          _ne?: { lng: number; lat: number }
        }
        const sw = bboxRaw._sw
        const ne = bboxRaw._ne
        if (sw?.lng != null && sw?.lat != null && ne?.lng != null && ne?.lat != null) {
          rawMap.fitBounds([sw.lng, sw.lat, ne.lng, ne.lat])
        } else {
          rawMap.fitBounds(myMapStore.bbox)
        }
        if (typeof myMapStore.restoreZoom === 'number') {
          rawMap.setZoom(myMapStore.restoreZoom)
          myMapStore.restoreZoom = null
        }
      }
    } else if (!myMapStore.mapSearch?.name) {
      // Only fit to full Cameroun when no commune is already resolved from the URL.
      // When ?commune=XYZ is present, applyCommuneFromQuery (MyMapView) sets mapSearch
      // before onMapReady fires; letting Map.vue's commune watcher handle the fit avoids
      // the visible Cameroun → commune flash.
      const fitEntireCameroon = () => {
        rawMap.resize()
        rawMap.fitBounds(createCameroonLngLatBounds(), { padding: CAMEROON_MAP_VIEW_FIT_PADDING })
      }
      // Call immediately (map is loaded at this point) and once more after a layout tick
      // to ensure the container has its final pixel dimensions.
      fitEntireCameroon()
      setTimeout(fitEntireCameroon, 150)
    }

    rawMap.on('moveend', () => {
      if (rawMap.getBounds()) {
        myMapStore.bbox = rawMap.getBounds()
      }
    })

    rawMap.on('idle', () => {
      myMapStore.mapCanvasToDataUrl = rawMap.getCanvas().toDataURL()
    })
  } catch (err) {
    console.error('[MyMap] onMapReady error:', err)
  }
}

onMounted(async () => {
  await nextTick()

  if (myMap.value) {
    myMapStore.myMap = myMap.value
  }
})

function onBasemapChanged(m: maplibregl.Map) {
  const sources = m.getStyle().sources
  for (const source in sources) {
    const tileSource = m.getSource(source)
    if (tileSource && tileSource.tileSize) {
      myMapStore.tileSize = tileSource.tileSize
    }
  }
}

watch(
  () => myMapStore.mapSearch?.bbox,
  (newValue) => {
    if (mapInstance.value == null || newValue == null) return
    if (myMapStore.mapSearch?.name != null) return
    mapInstance.value.fitBounds(newValue, { padding: 75 })
  },
)

watch(
  () => myMapStore.mapSearch?.coords,
  (newValue) => {
    if (mapInstance.value == null || newValue == null) return
    if (myMapStore.mapSearch?.name != null) return
    mapInstance.value.flyTo({
      center: [newValue.lng, newValue.lat],
      zoom: 10,
      speed: 0.5,
    })
  },
)

watch(
  () => myMapStore.mapSearch,
  (newValue) => {
    if (mapInstance.value != null && newValue === null) {
      mapInstance.value.fitBounds(createCameroonLngLatBounds(), {
        padding: CAMEROON_MAP_VIEW_FIT_PADDING,
      })
    }
  },
)
</script>

<style lang="scss">
.MyMap {
  width: 100%;
  height: 100%;
  position: relative;

  .MyMap__mapWrapper {
    position: relative;
    width: 100%;
    height: 100%;
  }

  .MyMap__map {
    width: 100%;
    height: 100%;

    .maplibregl-ctrl-top-right {
      align-items: flex-end;
    }
    .maplibregl-ctrl-bottom-right {
      align-items: flex-end;
    }
  }

  &.MyMap__mapWrapper--drawMode {
    .maplibregl-canvas-container canvas {
      cursor: crosshair !important;
    }
  }

  .MyMap__dropOverlay {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(0, 0, 0, 0.45);
    border: 2px dashed rgba(255, 255, 255, 0.5);
    border-radius: 0.375rem;
    pointer-events: none;
    z-index: 10;
    backdrop-filter: blur(4px);
  }

  .MyMap__dropOverlayContent {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.375rem;
    padding: 1.25rem 1.5rem;
    text-align: center;
  }

  .MyMap__dropOverlayText {
    font-size: 1.125rem;
    font-weight: 600;
    color: #fff;
    text-shadow: 0 1px 2px rgba(0, 0, 0, 0.5);
  }

  .MyMap__dropOverlayHint {
    font-size: 0.8125rem;
    font-weight: 400;
    color: rgba(255, 255, 255, 0.95);
    text-shadow: 0 1px 2px rgba(0, 0, 0, 0.5);
  }
}

.drop-overlay-enter-active,
.drop-overlay-leave-active {
  transition:
    opacity 0.2s ease,
    transform 0.2s ease;
}

.drop-overlay-enter-from,
.drop-overlay-leave-to {
  opacity: 0;
  transform: scale(0.98);
}

.drop-overlay-enter-to,
.drop-overlay-leave-from {
  opacity: 1;
  transform: scale(1);
}
</style>
